/**
 * The track-record ledger — one AG Grid row per `ExperienceItem`.
 *
 *   Property   address · `type · resolution`
 *   Numbers    purchase → sale (or → — while held)
 *   Credits    the parents it credits — role-prefixed pills
 *   Proof      "N docs" (hover lists them; one doc → click previews it) or ⚠ None
 *   Source     where the row came from (track record / extracted / manual / portable)
 *   Verified   Verified · Candidate pill (+ excluded marker)
 *
 * The left rail reads the row's standing: green once verified, amber while it
 * still needs a parent or proof. Reads only server fields (`verified`,
 * `source`); row click opens the item dialog. `quickFilterText` searches the
 * address, the parties and the source.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { FormPill } from "../../../components/forms/FormPrimitives";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { slate, status } from "../../../theme/colors";
import { FORM, formSx, px } from "../../../theme/formStyles";
import {
  CARD_GRID_ACTIVE_ROW_STYLE,
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_GRID_TWO_LINE_ROW_H,
  CARD_TABLE_BOX_SX,
} from "../shared/cardGrid";
import {
  ASSOCIATION_TYPES,
  docName,
  fmtPoints,
  itemUnits,
  unitsNeedNote,
  itemAddress,
  itemParents,
  itemPoints,
  missingCategory,
  needsAttention,
  SOURCES,
} from "./experienceConstants";

// Every cell in this ledger reads at 11px — the table is dense by design and
// the row content (address + type · resolution, two prices, party pills) has
// to fit without wrapping. Applied per cell AND on the grid so AG Grid's own
// `.ag-cell` rule (12.5px from the card-grid look) does not win.
const CELL = { fontSize: 11, lineHeight: "15px" };
const CELL_GRID_SX = {
  ...CARD_GRID_SX,
  "&& .ag-cell": { ...CARD_GRID_SX[".ag-cell"], ...CELL },
};

const PARENT_LABEL = {
  [ASSOCIATION_TYPES.BORROWERS]: "Borrower",
  [ASSOCIATION_TYPES.ENTITIES]: "Entity",
  [ASSOCIATION_TYPES.GENERAL_CONTRACTORS]: "GC",
};

/** A role-prefixed party pill: quiet role, then the name. */
export const PartyPill = ({ type, name }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: px(5),
      height: 20,
      px: px(8),
      borderRadius: px(11),
      bgcolor: FORM.color.surface2,
      border: `1px solid ${FORM.color.divider}`,
      ...formSx.meta,
      ...CELL,
      whiteSpace: "nowrap",
      maxWidth: "100%",
    }}
  >
    <Box component="span" sx={{ color: FORM.color.ink4 }}>
      {PARENT_LABEL[type] || type}
    </Box>
    <Box
      component="span"
      sx={{
        color: FORM.color.ink,
        fontWeight: 500,
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {name}
    </Box>
  </Box>
);

PartyPill.propTypes = { type: PropTypes.string, name: PropTypes.node };

const ExperienceItemsGrid = ({
  items,
  loan,
  byId,
  activeItemId,
  onRowClick,
  quickFilterText,
  emptyContent,
}) => {
  const dispatch = useDispatch();
  const docsById = useMemo(
    () => new Map((loan?.loanDocuments || []).map((d) => [String(d?._id), d])),
    [loan?.loanDocuments],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Property",
        colId: "property",
        // Rows size to their content: this cell and Credits opt into autoHeight,
        // so a row with several party pills grows and a two-line address never clips.
        autoHeight: true,
        minWidth: 220,
        flex: 1.6,
        valueGetter: (p) => itemAddress(p.data),
        cellRenderer: (p) => {
          const it = p.data || {};
          return (
            <Flx
              column
              g={0}
              sx={{
                py: px(FORM.s.s3),
                minWidth: 0,
                justifyContent: "center",
                minHeight: CARD_GRID_TWO_LINE_ROW_H,
              }}
            >
              <Txt
                sx={{
                  ...formSx.label,
                  ...CELL,
                  mb: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {p.value}
              </Txt>
              <Txt
                sx={{
                  ...formSx.meta,
                  ...CELL,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {[it.experienceType, it.resolutionStatus]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </Txt>
            </Flx>
          );
        },
      },
      {
        headerName: "Numbers",
        colId: "numbers",
        minWidth: 170,
        flex: 1.1,
        valueGetter: (p) => p.data?.propertyPurchasePrice ?? null,
        getQuickFilterText: () => "",
        cellRenderer: (p) => {
          const it = p.data || {};
          const sold = it.propertySalePrice != null;
          return (
            <Flx ac g={0.75} sx={{ height: "100%" }}>
              <Txt
                sx={{
                  ...formSx.body,
                  ...CELL,
                  fontVariantNumeric: "tabular-nums",
                  color: FORM.color.ink2,
                }}
              >
                {formatDollar(it.propertyPurchasePrice, "—")}
              </Txt>
              <Txt sx={{ ...formSx.meta, ...CELL, color: FORM.color.ink4 }}>
                →
              </Txt>
              <Txt
                sx={{
                  ...formSx.body,
                  ...CELL,
                  fontVariantNumeric: "tabular-nums",
                  color: sold ? FORM.color.ink : FORM.color.ink4,
                }}
              >
                {sold ? formatDollar(it.propertySalePrice) : "—"}
              </Txt>
            </Flx>
          );
        },
      },
      {
        headerName: "Credits",
        colId: "credits",
        autoHeight: true,
        minWidth: 220,
        flex: 1.6,
        sortable: false,
        valueGetter: (p) =>
          itemParents(p.data, byId)
            .map((x) => x.name)
            .join(" "),
        cellRenderer: (p) => {
          const parents = itemParents(p.data, byId);
          if (!parents.length)
            return (
              <Flx ac g={0.5} sx={{ minHeight: CARD_GRID_TWO_LINE_ROW_H }}>
                <WarningAmberOutlined
                  sx={{ fontSize: 14, color: status.warning.fg }}
                />
                <Txt sx={{ ...formSx.meta, ...CELL, color: status.warning.fg }}>
                  No parent
                </Txt>
              </Flx>
            );
          return (
            <Flx
              ac
              g={0.5}
              sx={{
                flexWrap: "wrap",
                py: px(FORM.s.s3),
                minWidth: 0,
                minHeight: CARD_GRID_TWO_LINE_ROW_H,
              }}
            >
              {parents.map((pt) => (
                <PartyPill
                  key={`${pt.type}-${pt.id}`}
                  type={pt.type}
                  name={pt.name}
                />
              ))}
            </Flx>
          );
        },
      },
      {
        headerName: "Proof",
        colId: "proof",
        minWidth: 96,
        flex: 0.6,
        sortable: false,
        getQuickFilterText: () => "",
        valueGetter: (p) => (p.data?.associations?.loanDocuments || []).length,
        cellRenderer: (p) => {
          const ids = p.data?.associations?.loanDocuments || [];
          if (!ids.length)
            return (
              <Flx ac g={0.5} sx={{ height: "100%" }}>
                <WarningAmberOutlined
                  sx={{ fontSize: 14, color: status.warning.fg }}
                />
                <Txt sx={{ ...formSx.meta, ...CELL, color: status.warning.fg }}>
                  None
                </Txt>
              </Flx>
            );
          const docs = ids
            .map((id) => docsById.get(String(id)))
            .filter(Boolean);
          const names = ids.map((id) => docName(docsById.get(String(id))));
          // A displayed file is always previewable: one doc previews on click,
          // several list on hover and open through the row (its Proof section).
          const single = docs.length === 1 ? docs[0] : null;
          return (
            <Tooltip title={names.join(" · ")}>
              <Flx
                ac
                g={0.5}
                onClick={
                  single
                    ? (e) => {
                        e.stopPropagation();
                        dispatch(filePreviewSetData(single));
                      }
                    : undefined
                }
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <DescriptionOutlined
                  sx={{ fontSize: 14, color: FORM.color.ink3 }}
                />
                <Txt sx={{ ...formSx.body, ...CELL, color: FORM.color.ink }}>
                  {ids.length} doc{ids.length === 1 ? "" : "s"}
                </Txt>
              </Flx>
            </Tooltip>
          );
        },
      },
      {
        headerName: "Source",
        colId: "source",
        minWidth: 110,
        flex: 0.7,
        valueGetter: (p) => (SOURCES[p.data?.source] || SOURCES.manual).label,
        cellRenderer: (p) => {
          const src = SOURCES[p.data?.source] || SOURCES.manual;
          return (
            <Flx ac sx={{ height: "100%" }}>
              <Tooltip title={src.hint}>
                <span>
                  <FormPill>{src.label}</FormPill>
                </span>
              </Tooltip>
            </Flx>
          );
        },
      },
      {
        // The guideline bucket and what it is worth. A verified row with no
        // category is counting a FULL point on trust, so it is flagged rather
        // than left blank — that is the one state a reviewer must fix.
        headerName: "Category",
        colId: "qualifyingCategory",
        autoHeight: true,
        minWidth: 170,
        flex: 1.1,
        valueGetter: (p) => p.data?.qualifyingCategory || "",
        cellRenderer: (p) => {
          const it = p.data || {};
          const flagged = missingCategory(it);
          return (
            <Flx
              column
              g={0}
              sx={{
                py: px(FORM.s.s3),
                minWidth: 0,
                justifyContent: "center",
                minHeight: CARD_GRID_TWO_LINE_ROW_H,
              }}
            >
              {it.qualifyingCategory ? (
                <>
                  <Txt
                    sx={{
                      ...formSx.label,
                      ...CELL,
                      mb: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.qualifyingCategory}
                  </Txt>
                  <Txt sx={{ ...formSx.meta, ...CELL }}>
                    {fmtPoints(itemPoints(it))} pts
                  </Txt>
                </>
              ) : (
                <Tooltip
                  title={
                    flagged
                      ? "Verified but not classified — it is counting 1.0 until someone picks a category."
                      : "Not classified yet — counts 1.0 until it is."
                  }
                >
                  <Flx ac g={0.5} sx={{ minWidth: 0 }}>
                    {flagged && (
                      <WarningAmberOutlined
                        sx={{ fontSize: 13, color: status.warning.fg }}
                      />
                    )}
                    <Txt
                      sx={{
                        ...formSx.meta,
                        ...CELL,
                        color: flagged ? status.warning.fg : FORM.color.ink4,
                        fontWeight: flagged ? 600 : 400,
                      }}
                    >
                      Not classified · 1.0 pts
                    </Txt>
                  </Flx>
                </Tooltip>
              )}
            </Flx>
          );
        },
      },
      {
        // Doors this address counts for (a condo sold unit by unit). Blank
        // means one; several with no note saying why is flagged.
        headerName: "Units",
        colId: "unitCount",
        minWidth: 84,
        flex: 0.5,
        headerClass: "num",
        valueGetter: (p) => itemUnits(p.data),
        getQuickFilterText: () => "",
        cellRenderer: (p) => {
          const n = itemUnits(p.data);
          const flagged = unitsNeedNote(p.data);
          return (
            <Flx ac jc g={0.5} sx={{ height: "100%" }}>
              {n > 1 ? (
                <Tooltip
                  title={
                    flagged
                      ? `Counts ${n} units but has no note explaining why. Open the item to add one.`
                      : `Counts ${n} units, each sold on its own.`
                  }
                >
                  <Flx ac g={0.5}>
                    {flagged ? (
                      <WarningAmberOutlined sx={{ fontSize: 13, color: status.warning.fg }} />
                    ) : null}
                    <Txt
                      sx={{
                        ...formSx.label,
                        ...CELL,
                        mb: 0,
                        color: flagged ? status.warning.fg : undefined,
                      }}
                    >
                      {n}
                    </Txt>
                  </Flx>
                </Tooltip>
              ) : (
                <Txt sx={{ ...formSx.meta, ...CELL, color: FORM.color.ink4 }}>1</Txt>
              )}
            </Flx>
          );
        },
      },
      {
        headerName: "Verified",
        colId: "verified",
        minWidth: 104,
        flex: 0.7,
        headerClass: "num",
        getQuickFilterText: () => "",
        valueGetter: (p) => (p.data?.verified ? 1 : 0),
        cellRenderer: (p) => {
          const it = p.data || {};
          return (
            <Flx ac g={0.5} sx={{ height: "100%", justifyContent: "flex-end" }}>
              {it.countedForExperience === false ? (
                <Tooltip title="Excluded from the rollup by UW">
                  <VisibilityOffOutlined
                    sx={{ fontSize: 14, color: FORM.color.ink4 }}
                  />
                </Tooltip>
              ) : null}
              <Tooltip
                title={
                  it.verified
                    ? `Verified by ${it.verifiedBy || "—"} · ${formatDateTime(it.verifiedDate, "datetime") || "—"}`
                    : "Candidate — not yet verified; does not count and is not persisted"
                }
              >
                <span>
                  <FormPill tone={it.verified ? "success" : "neutral"}>
                    {it.verified ? "Verified" : "Candidate"}
                  </FormPill>
                </span>
              </Tooltip>
            </Flx>
          );
        },
      },
    ],
    [byId, docsById, dispatch],
  );

  if (!items?.length) {
    return (
      <Box sx={CARD_TABLE_BOX_SX}>
        {emptyContent || (
          <Txt sx={{ display: "block", ...formSx.helper, p: px(FORM.s.s5) }}>
            No items.
          </Txt>
        )}
      </Box>
    );
  }

  return (
    <Box sx={CARD_TABLE_BOX_SX}>
      <AgGridTableWrapper
        compactTable
        noBorder
        suppressMinHeightWhenPopulated={items}
        sx={CELL_GRID_SX}
      >
        <AgGridReact
          rowData={items}
          columnDefs={columnDefs}
          defaultColDef={CARD_GRID_COL_DEF}
          getRowId={(p) => String(p.data?._id)}
          domLayout="autoHeight"
          quickFilterText={quickFilterText || undefined}
          onRowClicked={(e) => onRowClick?.(e.data)}
          // The left rail says where the row stands at a glance: green once UW
          // verified it, amber while it still needs a parent or proof. The
          // open-in-sidebar highlight wins over both.
          getRowStyle={(p) => {
            const rail = p.data?.verified
              ? status.success.solid
              : needsAttention(p.data)
                ? status.warning.solid
                : null;
            return {
              cursor: "pointer",
              ...(rail ? { boxShadow: `inset 3px 0 0 ${rail}` } : {}),
              ...(String(p.data?._id) === String(activeItemId)
                ? CARD_GRID_ACTIVE_ROW_STYLE
                : {}),
            };
          }}
          overlayNoRowsTemplate={`<span style="font-size:12.5px;color:${slate[500]}">No items match.</span>`}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

ExperienceItemsGrid.propTypes = {
  items: PropTypes.array,
  loan: PropTypes.object,
  byId: PropTypes.instanceOf(Map),
  activeItemId: PropTypes.string,
  onRowClick: PropTypes.func,
  quickFilterText: PropTypes.string,
  emptyContent: PropTypes.node,
};

export default ExperienceItemsGrid;
